import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { StorageFirebaseService } from '../services/firebase/storage-firebase.service';

/**
 * Interface for components that can be guarded by canDeactivateVehicle
 */
export interface CanDeactivateVehicle {
  vehicleSaved: () => boolean;
  preGeneratedVehicleId: () => string | null;
  isEditMode: () => boolean;
  uploadedPhotos: () => string[];
}

/**
 * Guard to clean up uploaded photos if user navigates away without saving
 * Only applies to add mode (not edit mode)
 */
export const canDeactivateVehicle: CanDeactivateFn<CanDeactivateVehicle> = async (component) => {
  // Allow navigation if:
  // - Vehicle was saved
  // - In edit mode (don't delete existing photos)
  // - No photos uploaded
  if (
    component.vehicleSaved() ||
    component.isEditMode() ||
    component.uploadedPhotos().length === 0
  ) {
    return true;
  }

  // Confirm navigation if there are unsaved uploaded photos
  const confirmed = confirm(
    'You have uploaded photos that will be lost if you leave. Are you sure you want to leave?',
  );

  if (confirmed) {
    // Clean up uploaded photos
    const storageService = inject(StorageFirebaseService);
    const vehicleId = component.preGeneratedVehicleId();
    const photos = component.uploadedPhotos();

    if (vehicleId && photos.length > 0) {
      try {
        // Get all Firebase URLs (skip blob: URLs)
        const firebaseUrls = photos.filter((url) => url.startsWith('https://'));

        if (firebaseUrls.length > 0) {
          console.log('🧹 Cleaning up uploaded photos on navigation away:', {
            vehicleId,
            photoCount: firebaseUrls.length,
          });

          await storageService.deleteFiles(firebaseUrls);

          console.log('✅ Uploaded photos cleaned up successfully');
        }
      } catch (error) {
        console.error('❌ Failed to clean up uploaded photos:', error);
        // Don't block navigation even if cleanup fails
      }
    }
  }

  return confirmed;
};
